import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { EnTetePage } from '../design/composants';
import { useAuth } from '../context/AuthContext';
import { Users, Clock, Calendar, Settings, Umbrella, Ban, Lock, CheckCircle, AlertTriangle, Clipboard, Layers, X, UserCog, Plus } from 'lucide-react';

const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
const VERT = '#1B5E20';
const VERT_L = '#E8F5E9';
const ROUGE = '#C62828';
const ROUGE_L = '#FFEBEE';
const GRIS = '#F8F9FA';
const GRIS_B = '#E0E0E0';

// ─── Labels FR ───────────────────────────────────────────────────────────────
const LABELS_MODULE = {
  employes:   'Employés',
  pointage:   'Pointage',
  conges:     'Congés',
  temps:      'Gestion du temps',
  parametres: 'Paramètres',
};

const LABELS_ACTION = {
  lire:      'Lire',
  creer:     'Créer',
  modifier:  'Modifier',
  supprimer: 'Supprimer',
  valider:   'Valider',
};

const LABELS_ROLE = {
  admin: 'Administrateur',
  directeur_general: 'Directeur Général',
  directeur_adjoint: 'Directeur Adjoint',
  drh: 'Directeur RH',
  rh_principal: 'RH Principal',
  assistant_rh: 'Assistant RH',
  prepose_regional: 'Préposé Régional',
  responsable_site: 'Responsable Site',
  chef_departement: 'Chef Département',
  chef_service: 'Chef Service',
  chef_equipe: 'Chef Équipe',
  employe: 'Employé',
};

const COULEUR_ROLE = {
  admin: '#4A148C',
  directeur_general: '#1A237E',
  directeur_adjoint: '#283593',
  drh: '#1B5E20',
  rh_principal: '#2E7D32',
  assistant_rh: '#388E3C',
  prepose_regional: '#E65100',
  responsable_site: '#BF360C',
  chef_departement: '#1565C0',
  chef_service: '#0277BD',
  chef_equipe: '#01579B',
  employe: '#757575',
};

// ─── Composants utilitaires ───────────────────────────────────────────────────
const BadgeRole = ({ role }) => (
  <span style={{
    display: 'inline-block', padding: '3px 10px', borderRadius: 20,
    fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
    background: (COULEUR_ROLE[role] || '#555') + '22',
    color: COULEUR_ROLE[role] || '#555',
    border: `1px solid ${(COULEUR_ROLE[role] || '#555')}44`,
  }}>
    {LABELS_ROLE[role] || role}
  </span>
);

const Card = ({ children, style = {} }) => (
  <div style={{
    background: '#fff', borderRadius: 12,
    border: `1px solid ${GRIS_B}`, padding: '20px 24px',
    marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', ...style,
  }}>
    {children}
  </div>
);

// ─── Cellule de permission dans la matrice ────────────────────────────────────
const CellPermission = ({ module, action, effective, roleDefault, override, onToggle, canEdit }) => {
  const hasOverride = override !== null;
  const isGranted = effective;

  // Source visuelle
  let cellBg = 'transparent';
  let borderColor = GRIS_B;
  let checkColor = '#bbb';
  let indicator = null;

  if (hasOverride) {
    if (override) {
      cellBg = '#F1F8E9';
      borderColor = '#A5D6A7';
      checkColor = VERT;
      indicator = <span title="Surcharge : accordée" style={{ fontSize: 9, color: VERT, marginLeft: 2 }}>+</span>;
    } else {
      cellBg = '#FFEBEE';
      borderColor = '#EF9A9A';
      checkColor = ROUGE;
      indicator = <span title="Surcharge : refusée" style={{ fontSize: 9, color: ROUGE, marginLeft: 2 }}>-</span>;
    }
  } else if (isGranted) {
    checkColor = VERT;
  }

  return (
    <td style={{ textAlign: 'center', padding: '8px 6px' }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, borderRadius: 6,
        background: cellBg, border: `1px solid ${borderColor}`,
        cursor: canEdit ? 'pointer' : 'default',
        transition: 'all 0.15s',
      }}
        onClick={() => canEdit && onToggle(module, action, effective, override)}
        title={canEdit ? (hasOverride ? 'Cliquer pour réinitialiser' : (isGranted ? 'Cliquer pour refuser' : 'Cliquer pour accorder')) : ''}
      >
        {isGranted
          ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12l5 5L20 7" stroke={checkColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          : <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#ccc" strokeWidth="2" strokeLinecap="round"/></svg>
        }
        {indicator}
      </div>
    </td>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────
export default function PermissionsEmployePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { employe: moi, token } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');
  const [nouveauRole, setNouveauRole] = useState('');
  const [msgOk, setMsgOk] = useState('');
  const [confirmPending, setConfirmPending] = useState(null);
  // Changement du rôle principal : acte distinct, motivé et journalisé côté serveur.
  const [rolePrincipal, setRolePrincipal] = useState('');
  const [motifRole, setMotifRole] = useState('');

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  // Rôles que l'utilisateur courant peut attribuer (tous sauf admin si non-admin)
  const peutGerer = ['admin', 'drh', 'rh_principal', 'directeur_general', 'directeur_adjoint'].includes(moi?.role);
  const peutVoir = peutGerer || ['assistant_rh', 'prepose_regional', 'responsable_site', 'chef_departement', 'chef_service', 'chef_equipe'].includes(moi?.role);

  const charger = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/permissions/${id}`, auth);
      setData(res.data);
    } catch (e) {
      setErreur(e.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); }, [id]);

  const flash = (msg) => { setMsgOk(msg); setTimeout(() => setMsgOk(''), 3000); };

  // ─── Changer le rôle principal (contrôlé côté serveur : rang, acte sur soi interdit, motif)
  const changerRolePrincipal = () => {
    if (!rolePrincipal || motifRole.trim().length < 5) return;
    setConfirmPending({
      titre: `Attribuer le rôle « ${LABELS_ROLE[rolePrincipal] || rolePrincipal} » ?`,
      message: "Le changement est journalisé avec votre nom et le motif saisi ; le salarié en est informé.",
      action: 'Confirmer',
      onConfirm: async () => {
        try {
          setSaving(true);
          await axios.put(`${API}/permissions/${id}/role`, { role: rolePrincipal, motif: motifRole.trim() }, auth);
          setRolePrincipal(''); setMotifRole('');
          await charger();
          flash('Rôle principal modifié');
        } catch (e) { setErreur(e.response?.data?.message || 'Erreur'); }
        finally { setSaving(false); }
      },
    });
  };

  // ─── Ajouter un rôle secondaire
  const ajouterRole = async () => {
    if (!nouveauRole) return;
    try {
      setSaving(true);
      await axios.post(`${API}/permissions/${id}/roles`, { role: nouveauRole }, auth);
      setNouveauRole('');
      await charger();
      flash('Rôle secondaire ajouté');
    } catch (e) {
      setErreur(e.response?.data?.message || 'Erreur');
    } finally { setSaving(false); }
  };

  // ─── Supprimer un rôle secondaire
  const supprimerRole = (role) => {
    setConfirmPending({ titre: `Retirer le rôle "${LABELS_ROLE[role] || role}" ?`, message: "Les accès liés à ce rôle seront supprimés.", action: 'Retirer', onConfirm: async () => {
      try { setSaving(true); await axios.delete(`${API}/permissions/${id}/roles/${role}`, auth); await charger(); flash('Rôle secondaire retiré'); }
      catch (e) { setErreur(e.response?.data?.message || 'Erreur'); }
      finally { setSaving(false); }
    }});
  };

  // ─── Basculer une permission (toggle)
  const togglePermission = async (module, action, currentEffective, currentOverride) => {
    if (!peutGerer) return;

    try {
      setSaving(true);
      // Si override existe → le supprimer (revenir au défaut du rôle)
      if (currentOverride !== null) {
        const perm = data.permissions_specifiques.find(p => p.module === module && p.action === action);
        if (perm) {
          await axios.delete(`${API}/permissions/${id}/specifiques/${perm.id}`, auth);
          await charger();
          flash('Surcharge supprimée — droits du rôle restaurés');
          return;
        }
      }
      // Sinon → créer une surcharge inverse
      await axios.put(`${API}/permissions/${id}/specifiques`, {
        module,
        action,
        accordee: !currentEffective,
      }, auth);
      await charger();
      flash(currentEffective ? 'Permission refusée (surcharge)' : 'Permission accordée (surcharge)');
    } catch (e) {
      setErreur(e.response?.data?.message || 'Erreur');
    } finally { setSaving(false); }
  };

  // ─── Réinitialiser tout un module
  const reinitModule = (module) => {
    if (!peutGerer) return;
    const permsModule = (data?.permissions_specifiques || []).filter(p => p.module === module);
    if (!permsModule.length) return;
    setConfirmPending({ titre: `Réinitialiser le module "${LABELS_MODULE[module]}" ?`, message: 'Toutes les surcharges seront supprimées. Les droits du rôle seront restaurés.', action: 'Réinitialiser', onConfirm: async () => {
      try { setSaving(true); for (const perm of permsModule) { await axios.delete(`${API}/permissions/${id}/specifiques/${perm.id}`, auth); } await charger(); flash('Module réinitialisé aux droits du rôle'); }
      catch (e) { setErreur(e.response?.data?.message || 'Erreur'); }
      finally { setSaving(false); }
    }});
  };

  if (!peutVoir) {
    return (
      <Layout>
        <div style={{ maxWidth: 600, margin: '40px auto', textAlign: 'center', padding: 32 }}>
          <div style={{ marginBottom: 16 }}><Ban size={48} color={ROUGE} /></div>
          <h2 style={{ color: ROUGE }}>Accès non autorisé</h2>
          <p style={{ color: '#666' }}>Vous n'avez pas les droits pour consulter les permissions.</p>
          <button onClick={() => navigate(-1)} style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', cursor: 'pointer', fontWeight: 700 }}>
            Retour
          </button>
        </div>
      </Layout>
    );
  }

  if (loading) return <Layout><div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Chargement...</div></Layout>;

  if (!data) return (
    <Layout>
      <div style={{ maxWidth: 600, margin: '40px auto', padding: 32 }}>
        <p style={{ color: ROUGE }}>{erreur || 'Données introuvables'}</p>
        <button onClick={() => navigate(-1)} style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', cursor: 'pointer' }}>Retour</button>
      </div>
    </Layout>
  );

  const { employe, roles_secondaires, permissions_specifiques, permissions_resolues, permissions_role, meta } = data;
  const modules = meta?.MODULES || [];
  const actions = meta?.ACTIONS || [];
  // Seuls les rôles que l'utilisateur courant est habilité à attribuer (calculés par le serveur).
  const attribuables = meta?.roles_attribuables || [];
  const estSoi = moi?.id === employe.id;
  const rolesDisponibles = (meta?.roles_secondaires_attribuables || []).filter(r =>
    r !== employe.role &&
    !roles_secondaires.some(rs => rs.role === r)
  );
  const rolesPrincipauxPossibles = attribuables.filter(r => r !== employe.role);

  const ROUGE_PERM = '#C62828';
  return (
    <Layout>
      {confirmPending && (<div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:9000,display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{background:'#fff',borderRadius:14,padding:'24px 28px',maxWidth:400,width:'calc(100% - 32px)',boxShadow:'0 8px 40px rgba(0,0,0,.18)'}}><div style={{fontSize:16,fontWeight:700,color:'#1A1A1A',marginBottom:8}}>{confirmPending.titre}</div><div style={{fontSize:13,color:'#666',marginBottom:24}}>{confirmPending.message}</div><div style={{display:'flex',gap:10,justifyContent:'flex-end'}}><button onClick={()=>setConfirmPending(null)} style={{padding:'10px 18px',border:'1px solid #e0e0e0',borderRadius:8,background:'#f5f5f5',cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:'inherit',minHeight:44}}>Annuler</button><button onClick={()=>{confirmPending.onConfirm();setConfirmPending(null);}} style={{padding:'10px 18px',border:'none',borderRadius:8,background:ROUGE_PERM,color:'#fff',cursor:'pointer',fontSize:13,fontWeight:600,fontFamily:'inherit',minHeight:44}}>{confirmPending.action}</button></div></div></div>)}
      <div className="rh-page" style={{ maxWidth: 1000 }}>

        {/* ─── En-tête (socle visuel Module 1) ─── */}
        <EnTetePage
          fil={[{ libelle: 'Employés', vers: '/employes' }, { libelle: `${employe.prenom || ''} ${(employe.nom || '').toUpperCase()}`.trim(), vers: `/employes/${id}` }, { libelle: 'Droits et accès' }]}
          titre="Droits et accès"
          sousTitre={`${employe.prenom || ''} ${(employe.nom || '').toUpperCase()}`.trim()}
          actions={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--sg-texte-3)' }}>Rôle principal : <BadgeRole role={employe.role} /></span>}
        />

        {/* Messages flash */}
        {msgOk && (
          <div style={{ background: VERT_L, border: `1px solid #A5D6A7`, borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: VERT, fontWeight: 600, fontSize: 13 }}>
            <CheckCircle size={14} style={{ marginRight: 6 }} />{msgOk}
          </div>
        )}
        {erreur && (
          <div style={{ background: ROUGE_L, border: `1px solid #EF9A9A`, borderRadius: 8, padding: '10px 16px', marginBottom: 16, color: ROUGE, fontSize: 13 }}>
            <AlertTriangle size={14} style={{ marginRight: 6, flexShrink: 0 }} />{erreur} <button onClick={() => setErreur('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: ROUGE, marginLeft: 8, display: 'inline-flex', alignItems: 'center' }}><X size={14} /></button>
          </div>
        )}

        {/* ─── Rôle principal ─── */}
        {!estSoi && rolesPrincipauxPossibles.length > 0 && (
          <Card>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <UserCog size={15} />Rôle principal
            </div>
            <div style={{ fontSize: 12, color: '#777', marginBottom: 14 }}>
              Le rôle détermine les écrans et les actions accessibles. Tout changement exige un motif et est journalisé.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <select value={rolePrincipal} onChange={e => setRolePrincipal(e.target.value)}
                style={{ flex: '1 1 220px', maxWidth: 280, padding: '8px 12px', border: `1px solid ${GRIS_B}`, borderRadius: 8, fontSize: 13, background: '#fff' }}>
                <option value="">— Nouveau rôle principal —</option>
                {rolesPrincipauxPossibles.map(r => <option key={r} value={r}>{LABELS_ROLE[r] || r}</option>)}
              </select>
              <input value={motifRole} onChange={e => setMotifRole(e.target.value)} placeholder="Motif (obligatoire, 5 caractères minimum)"
                style={{ flex: '2 1 260px', padding: '8px 12px', border: `1px solid ${GRIS_B}`, borderRadius: 8, fontSize: 13 }} />
              <button onClick={changerRolePrincipal} disabled={!rolePrincipal || motifRole.trim().length < 5 || saving} style={{
                background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                opacity: (!rolePrincipal || motifRole.trim().length < 5 || saving) ? 0.5 : 1,
              }}>Appliquer</button>
            </div>
          </Card>
        )}

        {/* ─── Rôles secondaires ─── */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Layers size={15} style={{ marginRight: 6 }} />Rôles secondaires
            </div>
            <span style={{ fontSize: 12, color: '#999' }}>Les rôles secondaires ajoutent leurs permissions à celles du rôle principal</span>
          </div>

          {roles_secondaires.length === 0 ? (
            <div style={{ color: '#bbb', fontSize: 13, marginBottom: 16 }}>Aucun rôle secondaire</div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {roles_secondaires.map(rs => (
                <div key={rs.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: GRIS, border: `1px solid ${GRIS_B}`, borderRadius: 20, padding: '4px 12px',
                }}>
                  <BadgeRole role={rs.role} />
                  {peutGerer && !estSoi && (
                    <button onClick={() => supprimerRole(rs.role)} disabled={saving} style={{
                      background: 'none', border: 'none', cursor: 'pointer', color: '#bbb',
                      fontSize: 14, padding: 0, lineHeight: 1,
                    }} title="Retirer ce rôle" aria-label="Retirer ce rôle"><X size={13} /></button>
                  )}
                </div>
              ))}
            </div>
          )}

          {peutGerer && !estSoi && rolesDisponibles.length > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select
                value={nouveauRole}
                onChange={e => setNouveauRole(e.target.value)}
                style={{ flex: 1, maxWidth: 280, padding: '8px 12px', border: `1px solid ${GRIS_B}`, borderRadius: 8, fontSize: 13, background: '#fff' }}
              >
                <option value="">— Choisir un rôle à ajouter —</option>
                {rolesDisponibles.map(r => (
                  <option key={r} value={r}>{LABELS_ROLE[r] || r}</option>
                ))}
              </select>
              <button onClick={ajouterRole} disabled={!nouveauRole || saving} style={{
                background: VERT, color: '#fff', border: 'none', borderRadius: 8,
                padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                opacity: (!nouveauRole || saving) ? 0.5 : 1,
              }}>
                <Plus size={13} style={{ marginRight: 4, verticalAlign: "middle" }} />Ajouter
              </button>
            </div>
          )}
        </Card>

        {/* ─── Matrice des permissions ─── */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 6 }}><Lock size={15} />Matrice des permissions</span>
          </div>

          {/* Légende */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666' }}>
              <div style={{ width: 14, height: 14, borderRadius: 3, background: VERT_L, border: `1px solid #A5D6A7` }}/>
              Surcharge accordée (+)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666' }}>
              <div style={{ width: 14, height: 14, borderRadius: 3, background: ROUGE_L, border: `1px solid #EF9A9A` }}/>
              Surcharge refusée (×)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666' }}>
              <div style={{ width: 14, height: 14, borderRadius: 3, background: '#fff', border: `1px solid ${GRIS_B}` }}/>
              Hérité du rôle
            </div>
            {peutGerer && (
              <div style={{ marginLeft: 'auto', fontSize: 12, color: '#999' }}>
                Cliquez sur une case pour modifier · Surcharge existante → clic pour réinitialiser
              </div>
            )}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: '#999', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: `2px solid ${GRIS_B}` }}>
                    Module
                  </th>
                  {actions.map(action => (
                    <th key={action} style={{ textAlign: 'center', padding: '8px 6px', color: '#555', fontWeight: 600, fontSize: 12, borderBottom: `2px solid ${GRIS_B}`, minWidth: 80 }}>
                      {LABELS_ACTION[action] || action}
                    </th>
                  ))}
                  {peutGerer && (
                    <th style={{ padding: '8px 6px', borderBottom: `2px solid ${GRIS_B}` }}/>
                  )}
                </tr>
              </thead>
              <tbody>
                {modules.map((module, idx) => {
                  const hasModuleOverride = permissions_specifiques.some(p => p.module === module);
                  return (
                    <tr key={module} style={{ background: idx % 2 === 0 ? '#FAFAFA' : '#fff' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1a1a1a', borderBottom: `1px solid ${GRIS_B}` }}>
                        <div>{LABELS_MODULE[module] || module}</div>
                        <div style={{ fontSize: 10, color: '#bbb', fontWeight: 400, marginTop: 2 }}>
                          Rôle : {(permissions_role[module] || []).map(a => LABELS_ACTION[a]).join(', ') || 'Aucun'}
                        </div>
                      </td>
                      {actions.map(action => {
                        const effective = (permissions_resolues[module] || []).includes(action);
                        const roleDefault = (permissions_role[module] || []).includes(action);
                        const overridePerm = permissions_specifiques.find(p => p.module === module && p.action === action);
                        const override = overridePerm ? overridePerm.accordee : null;

                        return (
                          <CellPermission
                            key={action}
                            module={module}
                            action={action}
                            effective={effective}
                            roleDefault={roleDefault}
                            override={override}
                            onToggle={togglePermission}
                            canEdit={peutGerer && !estSoi}
                          />
                        );
                      })}
                      {peutGerer && (
                        <td style={{ padding: '8px 6px', borderBottom: `1px solid ${GRIS_B}` }}>
                          {hasModuleOverride && (
                            <button
                              onClick={() => reinitModule(module)}
                              disabled={saving}
                              title="Réinitialiser toutes les surcharges de ce module"
                              style={{
                                background: 'none', border: `1px solid ${GRIS_B}`, borderRadius: 6,
                                padding: '3px 8px', fontSize: 11, color: '#999', cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              ↺ Reset
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* ─── Surcharges actives ─── */}
        {permissions_specifiques.length > 0 && (
          <Card>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', marginBottom: 16 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clipboard size={15} />Surcharges individuelles actives ({permissions_specifiques.length})</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {permissions_specifiques.map(perm => (
                <div key={perm.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  background: perm.accordee ? VERT_L : ROUGE_L,
                  border: `1px solid ${perm.accordee ? '#A5D6A7' : '#EF9A9A'}`,
                  borderRadius: 8, padding: '8px 14px',
                }}>
                  {perm.accordee ? <CheckCircle size={16} color={VERT} /> : <Ban size={16} color={ROUGE} />}
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: perm.accordee ? VERT : ROUGE }}>
                      {LABELS_MODULE[perm.module] || perm.module}
                    </span>
                    <span style={{ color: '#666', fontSize: 13 }}> → </span>
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a' }}>
                      {LABELS_ACTION[perm.action] || perm.action}
                    </span>
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#999' }}>
                      {perm.accordee ? 'accordée' : 'refusée'} par surcharge individuelle
                    </span>
                  </div>
                  {peutGerer && (
                    <button
                      onClick={async () => {
                        try {
                          setSaving(true);
                          await axios.delete(`${API}/permissions/${id}/specifiques/${perm.id}`, auth);
                          await charger();
                          flash('Surcharge supprimée');
                        } catch (e) { setErreur(e.response?.data?.message || 'Erreur'); }
                        finally { setSaving(false); }
                      }}
                      disabled={saving}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#bbb', fontSize: 18, padding: '0 4px',
                      }}
                      title="Supprimer cette surcharge"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* ─── Récapitulatif final ─── */}
        <Card style={{ background: '#FAFFFE', border: `1px solid #A5D6A7` }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: VERT, marginBottom: 16 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><CheckCircle size={15} />Permissions effectives (après résolution complète)</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
            {modules.map(module => {
              const actions_eff = permissions_resolues[module] || [];
              return (
                <div key={module} style={{ background: '#fff', borderRadius: 8, border: `1px solid ${GRIS_B}`, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a1a', marginBottom: 8 }}>
                    {LABELS_MODULE[module]}
                  </div>
                  {actions_eff.length === 0 ? (
                    <div style={{ fontSize: 12, color: '#bbb' }}>Aucun accès</div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {actions_eff.map(a => (
                        <span key={a} style={{
                          fontSize: 11, padding: '2px 7px', borderRadius: 20,
                          background: VERT_L, color: VERT, fontWeight: 600,
                        }}>{LABELS_ACTION[a]}</span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

      </div>
    </Layout>
  );
}
